import { useState, useEffect, useCallback } from "react";
import {
  getContacts, createContact, updateContact, deleteContact,
  bulkDeleteContacts, getDuplicates, getGroups, getFlatGroups, createGroup, updateGroup, deleteGroup,
  exportContacts
} from "../../mailerApi";
import "./Mailer.css";

/* Flatten tree groups for dropdowns */
function flattenGroups(groups, depth = 0) {
  let result = [];
  for (const g of groups) {
    result.push({ ...g, _depth: depth });
    if (g.children && g.children.length > 0) {
      result = result.concat(flattenGroups(g.children, depth + 1));
    }
  }
  return result;
}

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]); // tree structure
  const [flatGroupsList, setFlatGroupsList] = useState([]); // flat for dropdowns
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [editingContact, setEditingContact] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", phone: "", group: "", notes: "" });
  const [duplicates, setDuplicates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  // Group management
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: "", description: "", parent: "" });
  const [editingGroup, setEditingGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  function toggleExpand(id) {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function collectAllGroupIds(list) {
    const ids = [];
    const walk = (gs) => {
      for (const g of gs) {
        ids.push(g.id);
        if (g.children && g.children.length) walk(g.children);
      }
    };
    walk(list);
    return ids;
  }

  function expandAllGroups() {
    setExpandedGroups(new Set(collectAllGroupIds(groups)));
  }

  function collapseAllGroups() {
    setExpandedGroups(new Set());
  }

  const allGroupsTotal = groups.reduce((sum, g) => sum + (g.total_contact_count || 0), 0);

  const loadGroups = useCallback(async () => {
    try {
      const d = await getGroups();
      const tree = Array.isArray(d) ? d : d.results || [];
      setGroups(tree);
      setFlatGroupsList(flattenGroups(tree));
    } catch (e) { /* ignore */ }
  }, []);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (groupFilter) params.set("group", groupFilter);
      params.set("page", page);
      params.set("page_size", pageSize);
      const data = await getContacts(params.toString());
      if (Array.isArray(data)) {
        setContacts(data);
        setTotalCount(data.length);
      } else {
        setContacts(data.results || []);
        setTotalCount(data.count || 0);
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [search, groupFilter, page, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, groupFilter]);

  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  function openCreate() {
    setEditingContact(null);
    setForm({ email: "", name: "", phone: "", group: "", notes: "" });
    setShowForm(true);
  }

  function openEdit(c) {
    setEditingContact(c);
    setForm({ email: c.email, name: c.name || "", phone: c.phone || "", group: c.group || "", notes: c.notes || "" });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.email.trim()) {
      setError("ელ. ფოსტა სავალდებულოა");
      return;
    }
    try {
      const payload = { ...form, group: form.group || null };
      if (editingContact) {
        await updateContact(editingContact.id, payload);
      } else {
        await createContact(payload);
      }
      setShowForm(false);
      loadContacts();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("წავშალოთ ეს კონტაქტი?")) return;
    try {
      await deleteContact(id);
      loadContacts();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleBulkDelete() {
    if (!selected.size) return;
    if (!window.confirm(`წავშალოთ ${selected.size} კონტაქტი?`)) return;
    try {
      await bulkDeleteContacts([...selected]);
      setSelected(new Set());
      loadContacts();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (groupFilter) params.set("group", groupFilter);
      if (search) params.set("search", search);
      const activeGroup = flatGroupsList.find(g => String(g.id) === String(groupFilter));
      const base = activeGroup ? activeGroup.name : "ყველა კონტაქტი";
      const date = new Date().toISOString().slice(0, 10);
      await exportContacts(params.toString(), `${base} ${date}.xlsx`);
    } catch (e) {
      setError(e.message);
    }
    setExporting(false);
  }

  async function handleFindDuplicates() {
    try {
      const data = await getDuplicates();
      setDuplicates(data);
    } catch (e) {
      setError(e.message);
    }
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === contacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(contacts.map(c => c.id)));
    }
  }

  // ─── Group management ───
  function openGroupCreate() {
    setEditingGroup(null);
    setGroupForm({ name: "", description: "", parent: "" });
    setShowGroupManager(true);
  }

  function openGroupEdit(g) {
    setEditingGroup(g);
    setGroupForm({ name: g.name, description: g.description || "", parent: g.parent || "" });
    setShowGroupManager(true);
  }

  async function handleGroupSave() {
    try {
      const payload = { ...groupForm, parent: groupForm.parent || null };
      if (editingGroup) {
        await updateGroup(editingGroup.id, payload);
      } else {
        await createGroup(payload);
      }
      setShowGroupManager(false);
      loadGroups();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleGroupDelete(id) {
    if (!window.confirm("წავშალოთ ეს ჯგუფი?")) return;
    try {
      await deleteGroup(id);
      loadGroups();
    } catch (e) {
      setError(e.message);
    }
  }

  function renderGroupsTree(groupList, depth = 0) {
    return groupList.map(g => {
      const hasChildren = g.children && g.children.length > 0;
      const isExpanded = expandedGroups.has(g.id);
      const isActive = String(groupFilter) === String(g.id);
      return (
        <div key={g.id} className="group-tree-branch">
          <div
            className={`group-tree-row ${isActive ? "active" : ""}`}
            style={{ paddingLeft: 8 + depth * 16 }}
          >
            {hasChildren ? (
              <button
                className="tree-toggle"
                onClick={(e) => { e.stopPropagation(); toggleExpand(g.id); }}
                title={isExpanded ? "ჩაკეცვა" : "გაშლა"}
              >
                {isExpanded ? "▾" : "▸"}
              </button>
            ) : (
              <span className="tree-toggle-spacer" />
            )}
            <span
              className="group-tree-name"
              onClick={() => setGroupFilter(isActive ? "" : String(g.id))}
              title="ფილტრი"
            >
              {g.name}
            </span>
            <span className="group-tree-count">{g.total_contact_count}</span>
            <span className="group-tree-actions">
              <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openGroupEdit(g); }} title="რედაქტირება">&#9998;</button>
              <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleGroupDelete(g.id); }} title="წაშლა">&#128465;</button>
            </span>
          </div>
          {hasChildren && isExpanded && (
            <div className="group-tree-children">
              {renderGroupsTree(g.children, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  }

  function renderGroupOptions(groupList) {
    return groupList.map(g => (
      <option key={g.id} value={g.id}>
        {g._depth > 0 ? "\u00A0\u00A0".repeat(g._depth) + "└ " : ""}{g.name}
      </option>
    ));
  }

  return (
    <div className="mailer-page contacts-page">
      <div className="mailer-header">
        <h1>კონტაქტები</h1>
        <div className="mailer-actions">
          <button
            className="btn btn-secondary"
            onClick={handleExport}
            disabled={exporting}
            title={groupFilter ? "არჩეული ჯგუფის ექსპორტი" : "ყველა კონტაქტის ექსპორტი ჯგუფების მიხედვით"}
          >
            {exporting ? "ექსპორტი..." : "⬇ ექსპორტი"}
          </button>
          <button className="btn btn-secondary" onClick={handleFindDuplicates}>
            დუბლიკატები
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            + დამატება
          </button>
        </div>
      </div>

      {error && <div className="mailer-error">{error}<button onClick={() => setError("")}>×</button></div>}

      {/* Duplicates panel */}
      {duplicates && (
        <div className="duplicates-panel">
          <div className="duplicates-header">
            <h3>დუბლიკატები ({duplicates.length})</h3>
            <button onClick={() => setDuplicates(null)}>×</button>
          </div>
          {duplicates.length === 0 ? (
            <p className="no-data">დუბლიკატები არ მოიძებნა</p>
          ) : (
            duplicates.map((d, i) => (
              <div key={i} className="duplicate-group">
                <span className="dupe-email">{d.email}</span>
                <span className="dupe-count">{d.count} ჩანაწერი</span>
                <div className="dupe-items">
                  {d.contacts.map(c => (
                    <div key={c.id} className="dupe-item">
                      <span>{c.name || "—"}</span>
                      <span>{c.group__name || "—"}</span>
                      <button className="btn-icon" onClick={() => handleDelete(c.id)}>&#128465;</button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="contacts-layout">
        {/* Groups sidebar */}
        <aside className="groups-sidebar">
          <div className="groups-sidebar-header">
            <h3>ჯგუფები</h3>
            <div className="groups-sidebar-tools">
              <button className="btn-link-sm" onClick={expandAllGroups} title="ყველას გაშლა">გაშლა</button>
              <button className="btn-link-sm" onClick={collapseAllGroups} title="ყველას ჩაკეცვა">ჩაკეცვა</button>
              <button className="btn-link-sm primary" onClick={openGroupCreate} title="ახალი ჯგუფი">+ ჯგუფი</button>
            </div>
          </div>
          <div className="groups-tree-scroll">
            <div
              className={`all-groups-row ${groupFilter === "" ? "active" : ""}`}
              onClick={() => setGroupFilter("")}
            >
              <span className="all-groups-name">ყველა ჯგუფი</span>
              <span className="all-groups-count">{allGroupsTotal.toLocaleString("ka-GE")}</span>
            </div>
            {renderGroupsTree(groups)}
          </div>
        </aside>

        {/* Main content */}
        <div className="contacts-main">
          <div className="mailer-filters">
            <div className="search-wrap">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input
                placeholder="ძებნა მეილით ან სახელით..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {selected.size > 0 && (
              <button className="btn btn-danger" onClick={handleBulkDelete}>
                წაშლა ({selected.size})
              </button>
            )}
          </div>

      {/* Contacts table */}
      <div className="mailer-table-wrap">
        <table className="mailer-table contacts-table">
          <colgroup>
            <col className="col-check" />
            <col className="col-email" />
            <col className="col-name" />
            <col className="col-phone" />
            <col className="col-group" />
            <col className="col-status" />
            <col className="col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th><input type="checkbox" checked={selected.size === contacts.length && contacts.length > 0} onChange={toggleAll} /></th>
              <th>ელ. ფოსტა</th>
              <th>სახელი</th>
              <th>ტელეფონი</th>
              <th>ჯგუფი</th>
              <th>სტატუსი</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="no-data">იტვირთება...</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan="7" className="no-data">კონტაქტები არ მოიძებნა</td></tr>
            ) : contacts.map(c => (
              <tr key={c.id} className={selected.has(c.id) ? "selected" : ""}>
                <td><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} /></td>
                <td className="email-cell" title={c.email}>{c.email}</td>
                <td className="ellipsis-cell" title={c.name || ""}>{c.name || "—"}</td>
                <td className="phone-cell" title={c.phone || ""}>{c.phone || "—"}</td>
                <td className="ellipsis-cell" title={c.group_name || ""}>{c.group_name || "—"}</td>
                <td>
                  <span className={`status-badge ${c.is_active ? "active" : "inactive"}`}>
                    {c.is_active ? "აქტიური" : "არააქტიური"}
                  </span>
                </td>
                <td className="actions-cell">
                  <button className="btn-icon" onClick={() => openEdit(c)} title="რედაქტირება">&#9998;</button>
                  <button className="btn-icon" onClick={() => handleDelete(c.id)} title="წაშლა">&#128465;</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mailer-footer-info pagination-bar">
        <span>
          სულ: <strong>{totalCount}</strong> კონტაქტი
          {totalCount > pageSize && (
            <> &nbsp;|&nbsp; გვერდი {page} / {Math.max(1, Math.ceil(totalCount / pageSize))}</>
          )}
        </span>
        {totalCount > pageSize && (
          <div className="pagination-controls">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setPage(1)}
              disabled={page === 1 || loading}
            >« პირველი</button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
            >‹ წინა</button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page * pageSize >= totalCount || loading}
            >შემდეგი ›</button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setPage(Math.max(1, Math.ceil(totalCount / pageSize)))}
              disabled={page * pageSize >= totalCount || loading}
            >ბოლო »</button>
          </div>
        )}
      </div>
        </div>
      </div>

      {/* Add/Edit contact modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingContact ? "რედაქტირება" : "ახალი კონტაქტი"}</h2>
            <div className="form-group">
              <label>ელ. ფოსტა</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>სახელი</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div className="form-group">
              <label>ტელეფონი</label>
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+995 ..." />
            </div>
            <div className="form-group">
              <label>ჯგუფი</label>
              <select value={form.group} onChange={e => setForm({...form, group: e.target.value})}>
                <option value="">არცერთი</option>
                {renderGroupOptions(flatGroupsList)}
              </select>
            </div>
            <div className="form-group">
              <label>შენიშვნა</label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>გაუქმება</button>
              <button className="btn btn-primary" onClick={handleSave}>შენახვა</button>
            </div>
          </div>
        </div>
      )}

      {/* Group create/edit modal */}
      {showGroupManager && (
        <div className="modal-overlay" onClick={() => setShowGroupManager(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingGroup ? "ჯგუფის რედაქტირება" : "ახალი ჯგუფი"}</h2>
            <div className="form-group">
              <label>სახელი</label>
              <input
                value={groupForm.name}
                onChange={e => setGroupForm({...groupForm, name: e.target.value})}
                placeholder="მაგ. კლიენტები, პარტნიორები..."
              />
            </div>
            <div className="form-group">
              <label>მშობელი ჯგუფი (არასავალდებულო)</label>
              <select value={groupForm.parent} onChange={e => setGroupForm({...groupForm, parent: e.target.value})}>
                <option value="">არცერთი (ძირითადი ჯგუფი)</option>
                {flatGroupsList.filter(g => !editingGroup || g.id !== editingGroup.id).map(g => (
                  <option key={g.id} value={g.id}>
                    {g._depth > 0 ? "\u00A0\u00A0".repeat(g._depth) + "└ " : ""}{g.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>აღწერა (არასავალდებულო)</label>
              <textarea
                value={groupForm.description}
                onChange={e => setGroupForm({...groupForm, description: e.target.value})}
                rows={2}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowGroupManager(false)}>გაუქმება</button>
              <button className="btn btn-primary" onClick={handleGroupSave} disabled={!groupForm.name.trim()}>
                შენახვა
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
